/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/utils/importHelpers.js
 *
 * Description:
 * Helper functions used by Prospect Import.
 *
 * This file contains only formatting and helper functions.
 * No UI code.
 * No API calls.
 * No business logic.
 * ============================================================================
 */

import { DEFAULT_SUMMARY } from "../constants/campaignImportConstants";

/**
 * ============================================================================
 * Clone Records
 * ============================================================================
 */
export const cloneRecords = (records = []) => {
    return records.map(record => ({
        ...record
    }));
};

/**
 * ============================================================================
 * Initialize Grid Records
 * Adds "selected" property to every record.
 * ============================================================================
 */
export const prepareGridRecords = (records = []) => {

    return records.map(record => ({
        ...record,
        selected: false
    }));

};

/**
 * ============================================================================
 * Toggle Record Selection
 * ============================================================================
 */
export const toggleRecordSelection = (
    records = [],
    recordId
) => {

    return records.map(record => {

        if (record.id === recordId) {

            return {
                ...record,
                selected: !record.selected
            };

        }

        return record;

    });

};

/**
 * ============================================================================
 * Single Selection
 *
 * Used for Compare / Edit / View
 * ============================================================================
 */
export const selectSingleRecord = (
    records = [],
    recordId
) => {

    return records.map(record => ({

        ...record,

        selected: record.id === recordId

    }));

};

/**
 * ============================================================================
 * Select All Records
 * ============================================================================
 */
export const selectAllRecords = (
    records = [],
    selected = true
) => {

    return records.map(record => ({
        ...record,
        selected
    }));

};

/**
 * ============================================================================
 * Clear Selection
 * ============================================================================
 */
export const clearSelection = (
    records = []
) => {

    return records.map(record => ({
        ...record,
        selected: false
    }));

};

/**
 * ============================================================================
 * Get Selected Records
 * ============================================================================
 */
export const getSelectedRecords = (
    records = []
) => {

    return records.filter(record => record.selected);

};

/**
 * ============================================================================
 * Get Selected Record
 * (First Record)
 * ============================================================================
 */
export const getSelectedRecord = (
    records = []
) => {

    return records.find(record => record.selected) || null;

};

/**
 * ============================================================================
 * Search Records
 * ============================================================================
 */
export const searchRecords = (
    records = [],
    keyword = ""
) => {

    if (!keyword) {

        return records;

    }

    const search = keyword.toLowerCase();

    return records.filter(record =>

        String(record.name || "")
            .toLowerCase()
            .includes(search)

        ||

        String(record.external_id || "")
            .toLowerCase()
            .includes(search)

        ||

        String(record.status || "")
            .toLowerCase()
            .includes(search)

        ||

        String(record.remarks || "")
            .toLowerCase()
            .includes(search)

    );

};

/**
 * ============================================================================
 * Filter By Status
 * ============================================================================
 */
export const filterByStatus = (
    records = [],
    status = "ALL"
) => {

    if (status === "ALL") {

        return records;

    }

    return records.filter(
        record => record.status === status
    );

};

/**
 * ============================================================================
 * Format Validation Summary
 * ============================================================================
 */
export const formatValidationSummary = (
    summary = DEFAULT_SUMMARY
) => {

    return {

        totalRows: summary.total_rows || 0,

        validRows: summary.valid_rows || 0,

        invalidRows: summary.invalid_rows || 0,

        duplicateRows: summary.duplicate_rows || 0,

        warningRows: summary.warning_rows || 0

    };

};

/**
 * ============================================================================
 * Calculate Success Percentage
 * ============================================================================
 */
export const calculateSuccessPercentage = (
    summary = DEFAULT_SUMMARY
) => {

    if (!summary.total_rows) {

        return 0;

    }

    return Math.round(
        (summary.valid_rows / summary.total_rows) * 100
    );

};

/**
 * ============================================================================
 * Sort Records
 * ============================================================================
 */
export const sortRecords = (
    records = [],
    field,
    ascending = true
) => {

    const sorted = [...records];

    sorted.sort((a, b) => {

        if (a[field] < b[field]) {

            return ascending ? -1 : 1;

        }

        if (a[field] > b[field]) {

            return ascending ? 1 : -1;

        }

        return 0;

    });

    return sorted;

};

/**
 * ============================================================================
 * Reset Import State
 * ============================================================================
 */
export const getInitialImportState = () => {

    return {

        file: null,

        records: [],

        summary: DEFAULT_SUMMARY,

        verified: false,

        loading: false,

        importing: false,

        selectedRecord: null

    };

};